import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Faq, FeatureProofs, FinalCta, PatientTeaser } from "@/components/marketing/HomeSections";
import { CareSection, CoordinateSection, EcosystemSection, HeroChainVisual, ProblemSection, ReferencesSection, ResponseSection, TrustSection, UnderstandSection, WhoDoesWhat } from "@/components/marketing/HomeStorySections";
import { DEMO_PATH, PublicFooter, PublicHeader } from "@/components/marketing/PublicHeader";
import { ctaPrimary, ctaSecondary, useHashScroll, useSeo } from "@/components/marketing/MarketingPrimitives";
import ScrollToTopButton from "@/components/marketing/ScrollToTopButton";
import { Link } from "react-router-dom";

const CREDIBILITY = ["Déployé en oncologie", "Rôles séparés patient / IDEC / médecin / établissement", "Données hébergées et gouvernées, conformité CNDP"];

function Hero() {
  return (
    <section className="relative px-5 py-20 sm:py-24 lg:px-8 lg:py-28" data-testid="public-hero-section">
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-slate-50 via-white to-emerald-50/30" />
      <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[1.02fr_0.98fr]">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} data-testid="hero-copy-block">
          <p className="mb-6 text-xs font-extrabold uppercase tracking-[0.22em] text-teal-500" data-testid="hero-eyebrow-badge">Infrastructure numérique de coordination du parcours patient · Oncologie</p>
          <h1 className="font-display text-4xl font-bold leading-none tracking-tight text-slate-800 sm:text-5xl lg:text-6xl" data-testid="hero-main-title">Le parcours du patient ne s’arrête pas à la porte de la clinique.</h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-500" data-testid="hero-main-description">HANA structure et coordonne le suivi des patients en oncologie entre les consultations, les traitements et les différents acteurs de leur parcours. Les professionnels de santé gardent la décision clinique.</p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-testid="hero-cta-row">
            <Link to={DEMO_PATH} className={ctaPrimary} data-testid="hero-demo-button">Demander une démonstration <ArrowRight className="h-4 w-4" /></Link>
            <a href="#comment-ca-marche" className={ctaSecondary} data-testid="hero-how-button">Voir comment ça marche</a>
          </div>
          <ul className="mt-10 flex max-w-2xl flex-wrap gap-x-6 gap-y-2" data-testid="hero-credibility-row">
            {CREDIBILITY.map((item, i) => (
              <motion.li key={item} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + i * 0.1, duration: 0.5 }} className="flex items-center gap-2 text-sm font-semibold text-slate-500" data-testid={`hero-credibility-${i + 1}`}>
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />{item}
              </motion.li>
            ))}
          </ul>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}><HeroChainVisual /></motion.div>
      </div>
    </section>
  );
}

function HanaPublicHome() {
  useSeo("HANA — Infrastructure de coordination du parcours patient en oncologie", "HANA structure et coordonne le suivi des patients en oncologie entre les consultations, les traitements et les acteurs du parcours : informations structurées, coordination IDEC, priorisation, décision médicale tracée.");
  useHashScroll();
  return (
    <main className="min-h-screen bg-white font-sans text-slate-800 [overflow-x:clip]" data-testid="hana-public-home-page">
      <PublicHeader current="/" />
      <Hero />
      <ProblemSection />
      <ResponseSection />
      <CareSection />
      <CoordinateSection><FeatureProofs /></CoordinateSection>
      <UnderstandSection />
      <EcosystemSection />
      <WhoDoesWhat />
      <TrustSection />
      <div className="pt-20"><ReferencesSection testId="home-references-section" /></div>
      <PatientTeaser />
      <Faq />
      <FinalCta />
      <PublicFooter />
      <ScrollToTopButton />
    </main>
  );
}

export default HanaPublicHome;
